'use client';

import { useState, useEffect } from 'react';
import { sanitizeJobHtml } from '@/lib/sanitize-html';
import Link from 'next/link';
import { jobsApi } from '@/lib/api';
import { captureJobAlertClick } from '@/lib/job-alert-attribution';
import {
  ArrowLeft,
  MapPin,
  Clock,
  Building2,
  IndianRupee,
  Briefcase,
  Users,
  Globe,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { CompanyLogo } from '@/components/ui/company-logo';
import type { JobDetail } from '@/types';
import {
  publicEmploymentTypeLabel,
  publicExperienceLevelLabel,
  normalizePublicJobDescription,
  publicWorkModeLabel,
} from '@vhire/contracts/public-job-display';
import { jobDescriptionToHtml } from '@vhire/contracts/job-description-format';

// Helper to ensure a value is an array (handles JSON strings, null, undefined)
function ensureArray(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}


// Decode HTML entities to proper characters
function decodeHtmlEntities(html: string): string {
  const entities: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&apos;': "'",
    '&#39;': "'",
    '&#x27;': "'",
    '&nbsp;': ' ',
    '&#160;': ' ',
    '&#8217;': "'",    // Right single quotation mark
    '&#8216;': "'",    // Left single quotation mark
    '&#8220;': '"',    // Left double quotation mark
    '&#8221;': '"',    // Right double quotation mark
    '&#8211;': '–',    // En dash
    '&#8212;': '—',    // Em dash
    '&#8230;': '...',  // Ellipsis
    '&#8226;': '•',    // Bullet
    '&#x2019;': "'",   // Right single quotation (hex)
    '&#x2018;': "'",   // Left single quotation (hex)
    '&#x201C;': '"',   // Left double quotation (hex)
    '&#x201D;': '"',   // Right double quotation (hex)
  };

  let decoded = html;
  for (const [entity, char] of Object.entries(entities)) {
    decoded = decoded.replace(new RegExp(entity, 'gi'), char);
  }

  // Decode any remaining numeric entities (&#NNN; format)
  decoded = decoded.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)));
  // Decode hex entities (&#xHHHH; format)
  decoded = decoded.replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));

  return decoded;
}

// Sanitize HTML to prevent XSS and fix encoding issues
function sanitizeHtml(html: string): string {
  if (!html) return '';

  // First decode HTML entities, then give a PLAIN-TEXT description its markup
  // back: descriptions authored in the job wizard (and many ATS imports) are
  // typed text, and rendering those as HTML collapsed every newline, so the
  // whole JD arrived as one paragraph with its bullet markers inline. Runs
  // after the punctuation repair above so a recovered "• " is seen as a bullet,
  // and leaves input that is already HTML untouched.
  let cleaned = jobDescriptionToHtml(decodeHtmlEntities(normalizePublicJobDescription(html)));

  // Fix encoding issues (Windows-1252 / MS Office) — content cleanup, not security
  cleaned = cleaned
    .replace(/\uFFFD/g, '•')
    .replace(/[Â·•�]+/g, '•')
    .replace(/â€"/g, '–')
    .replace(/â€"/g, '—')
    .replace(/â€œ/g, '"')
    .replace(/â€/g, '"')
    .replace(/<o:p[^>]*>[\s\S]*?<\/o:p>/gi, '')
    .replace(/<!--\[if[^>]*>[\s\S]*?<!\[endif\]-->/gi, '')
    .replace(/<table[^>]*class="MsoNormalTable"[^>]*>/gi, '<div class="jd-content">')
    .replace(/<\/table>/gi, '</div>')
    .replace(/<tbody>/gi, '').replace(/<\/tbody>/gi, '')
    .replace(/<tr[^>]*>/gi, '<div class="jd-row">').replace(/<\/tr>/gi, '</div>')
    .replace(/<td[^>]*>/gi, '').replace(/<\/td>/gi, '')
    .replace(/class="Mso[^"]*"/gi, '')
    .replace(/style="[^"]*mso-[^"]*"/gi, '')
    .replace(/mso-[^;:]+:[^;]+;?/gi, '')
    .replace(/<p[^>]*>\s*<\/p>/gi, '')
    .replace(/\n\s*\n/g, '\n')
    .trim();

  // Delegate XSS allowlisting to the shared sanitizer (single source of truth);
  // the MS-Office cleanup above is content normalization, not security.
  return sanitizeJobHtml(cleaned);
}

function formatJobSalary(job: JobDetail): string {
  return job.salaryRange || 'Not disclosed';
}

// Fix text encoding issues (for titles and other plain text)
function sanitizeText(text: string | null | undefined): string {
  if (!text) return '';

  return text
    // Fix common encoding issues
    .replace(/\?/g, (match, offset, str) => {
      // Only replace ? if it looks like a broken dash (surrounded by spaces or alphanumeric)
      const before = str[offset - 1];
      const after = str[offset + 1];
      if (before === ' ' && after === ' ') return '–';
      return match;
    })
    .replace(/â€"/g, '–')              // Broken en-dash
    .replace(/â€"/g, '—')              // Broken em-dash
    .replace(/â€œ/g, '"')              // Broken left quote
    .replace(/â€/g, '"')               // Broken right quote
    .replace(/â€™/g, "'")              // Broken apostrophe
    .replace(/Â/g, '')                 // Stray  character
    .replace(/\uFFFD/g, '-')           // Replacement character → dash
    .trim();
}

// Format experience for display - prefer original ATS range if available
function formatExperience(
  experienceRange: string | null | undefined,
  experienceLevel: JobDetail['experienceLevel'],
): string {
  // If we have the original ATS experience range, use it
  if (experienceRange) {
    return experienceRange;
  }

  return publicExperienceLevelLabel(experienceLevel) ?? 'Not specified';
}

// Use dynamic time calculation
function formatTimeAgo(timestamp: number, now?: number): string {
  // Use provided 'now' or current time
  const currentTime = now || Math.floor(Date.now() / 1000);
  const diff = currentTime - timestamp;
  const days = Math.floor(diff / 86400);

  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function CollapsibleSection({ title, icon, children, defaultOpen = true }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {icon}
          <h3 className="font-semibold">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-0">
          {children}
        </div>
      )}
    </div>
  );
}

// Format company description with proper paragraphs and bullet points
function formatCompanyDescription(description: string): React.ReactNode {
  if (!description) return null;
  
  // Split by double newlines or common separators
  const paragraphs = description
    .split(/\n\n|(?<=\.)\s*(?=[A-Z])|(?:•|\*|-)\s*/)
    .map(p => p.trim())
    .filter(p => p.length > 0);
  
  // If no natural breaks found, try to split long text into readable chunks
  if (paragraphs.length === 1 && description.length > 300) {
    // Split on sentence boundaries
    const sentences = description.match(/[^.!?]+[.!?]+/g) || [description];
    const chunks: string[] = [];
    let currentChunk = '';
    
    sentences.forEach(sentence => {
      if (currentChunk.length + sentence.length > 250 && currentChunk.length > 0) {
        chunks.push(currentChunk.trim());
        currentChunk = sentence;
      } else {
        currentChunk += sentence;
      }
    });
    if (currentChunk.trim()) chunks.push(currentChunk.trim());
    
    return chunks.map((chunk, i) => (
      <p key={i} className="mb-2 last:mb-0">{chunk}</p>
    ));
  }
  
  return paragraphs.map((para, i) => (
    <p key={i} className="mb-2 last:mb-0">{para}</p>
  ));
}

interface CompanyInfoCardProps {
  companyInfo: NonNullable<JobDetail['companyInfo']>;
}

function CompanyInfoCard({ companyInfo }: CompanyInfoCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const descriptionLength = companyInfo.description?.length || 0;
  const shouldTruncate = descriptionLength > 400;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">About {companyInfo.name}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        {companyInfo.description && (
          <div>
            <div 
              className={`text-sm text-muted-foreground leading-relaxed relative ${
                shouldTruncate && !isExpanded ? 'max-h-32 overflow-hidden' : ''
              }`}
            >
              {formatCompanyDescription(companyInfo.description)}
              {shouldTruncate && !isExpanded && (
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-card to-transparent pointer-events-none" />
              )}
            </div>
            {shouldTruncate && (
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-3 flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 active:text-primary/70 transition-colors py-2 px-1 -ml-1 min-h-[44px] touch-manipulation"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Read more
                  </>
                )}
              </button>
            )}
          </div>
        )}
        
        {(companyInfo.industry || companyInfo.size) && (
          <>
            <Separator />
            <div className="space-y-2 text-sm">
              {companyInfo.industry && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Industry</span>
                  <span className="font-medium">{companyInfo.industry}</span>
                </div>
              )}
              {companyInfo.size && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Company size</span>
                  <span className="font-medium">{companyInfo.size}</span>
                </div>
              )}
            </div>
          </>
        )}
        
        {companyInfo.website && (
          <a
            href={companyInfo.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <ExternalLink className="w-4 h-4" />
            Visit website
          </a>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * SSR-fetched job data passed from the server component. Kept as a structural
 * type (not an `import type` from @vhire/shared) because pulling a type from
 * an external package into this 'use client' file broke webpack module
 * resolution in Next.js dev mode: "Cannot read properties of undefined
 * (reading 'call')" at the file's import-section eval. Whatever the shape on
 * the wire, `normalizeInitialJob` defensively normalizes it.
 */
type InitialJob = Record<string, unknown> | null | undefined;

interface JobDetailClientProps {
  slug: string;
  /**
   * SSR-fetched job data. When provided, this is used as the initial render
   * (no loading spinner, no waiting on a client fetch) and the client-side
   * refresh becomes a silent enhancement — a failed refresh no longer flips
   * the UI to "Job Not Found", which is what the Cloudflare AI Search
   * browser-rendering crawler was previously indexing.
   */
  initialJob?: InitialJob;
}

function normalizeInitialJob(initial: Record<string, unknown>): JobDetail {
  return {
    ...(initial as unknown as JobDetail),
    description: (initial.description as string | undefined) ?? '',
    responsibilities: ensureArray(initial.responsibilities),
    requirements: ensureArray(initial.requirements),
    benefits: ensureArray(initial.benefits),
    skills: ensureArray(initial.skills),
    status: (initial.status as string | undefined) ?? 'active',
  };
}

const countryNames = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'code' });
const displayCountry = (code: string) => /^[A-Z]{2}$/.test(code) ? countryNames.of(code) : code;

export default function JobDetailClient({ slug, initialJob }: JobDetailClientProps) {
  const initialNormalized = initialJob ? normalizeInitialJob(initialJob) : null;
  const [job, setJob] = useState<JobDetail | null>(initialNormalized);
  // Only show the loading skeleton when we have no SSR data to render.
  const [isLoading, setIsLoading] = useState(!initialNormalized);
  const [error, setError] = useState<string | null>(null);
  // Use state for current time to avoid hydration mismatch
  const [now, setNow] = useState<number>(Math.floor(Date.now() / 1000));

  useEffect(() => {
    setNow(Math.floor(Date.now() / 1000));
  }, []);

  useEffect(() => {
    // If SSR provided data, the client fetch is a silent refresh — failures
    // must not blank out the page or flip it to "Job Not Found", because the
    // user (and the crawler) are already looking at the SSR-rendered content.
    const hasInitial = initialNormalized !== null;
    let cancelled = false;
    async function fetchJob() {
      if (!hasInitial) setIsLoading(true);
      try {
        const response = await jobsApi.get(slug);
        if (cancelled) return;
        if (response.success && response.data) {
          // Normalize array fields that might come as JSON strings
          const jobData = {
            ...response.data,
            responsibilities: ensureArray(response.data.responsibilities),
            requirements: ensureArray(response.data.requirements),
            benefits: ensureArray(response.data.benefits),
            skills: ensureArray(response.data.skills),
          };
          setJob(jobData);
          setError(null);
        } else if (!hasInitial) {
          setError(response.error || 'Failed to load job details');
        }
      } catch (err) {
        if (cancelled) return;
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('[JobDetail] Failed to fetch job:', err);
        }
        if (!hasInitial) {
          setError('Unable to load job details. Please try again.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    fetchJob();
    return () => { cancelled = true; };
    // initialNormalized is derived from a prop; tracking the prop is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Job alert digest attribution (`?ja=<runId>`, plan D9): records `clicked`
  // once per (run, job) per session and remembers the run so a later
  // application for this job records `applied`. Fire-and-forget; never throws.
  useEffect(() => {
    captureJobAlertClick(slug, window.location.search);
  }, [slug]);

  // Count a rendered VHire visit, not a cacheable API read. One event per job
  // per browser session prevents refresh/navigation churn from masquerading as
  // unique interest while keeping SSR and crawlers out of the metric.
  useEffect(() => {
    const jobId = job?.id;
    if (!jobId) return;
    const storageKey = `vhire:job-view:${jobId}`;
    try {
      if (window.sessionStorage.getItem(storageKey)) {
        return;
      }
    } catch {
      // Storage can be unavailable in hardened/private browsing. The effect is
      // keyed by job id, so this mounted page still emits only once per job.
    }

    void jobsApi.recordView(jobId).then((recorded) => {
      if (!recorded) return;
      try { window.sessionStorage.setItem(storageKey, '1'); } catch { /* no-op */ }
    });
  }, [job?.id]);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-32 mb-6" />
          <div className="h-48 bg-muted rounded-2xl mb-6" />
          <div className="h-64 bg-muted rounded-xl" />
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Link href="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Jobs
        </Link>
        <Card className="p-8 text-center">
          <div className="text-muted-foreground mb-4">
            <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <h2 className="text-lg font-semibold text-foreground mb-2">Job Not Found</h2>
            <p className="text-sm">{error || 'This job posting may have been removed or is no longer available.'}</p>
          </div>
          <Button asChild variant="outline" className="mt-4">
            <Link href="/">Browse All Jobs</Link>
          </Button>
        </Card>
      </div>
    );
  }

  const locationLabel = job.locationLabel?.trim() || job.location?.trim() || null;
  const employmentTypeLabel = publicEmploymentTypeLabel(job.jobType);
  const experienceLevelLabel = publicExperienceLevelLabel(job.experienceLevel);
  const workModeLabel = publicWorkModeLabel(job.workMode ?? (job.remoteOk ? 'remote' : null));
  const postedDate = typeof job.postedDate === 'number' && job.postedDate > 0 ? job.postedDate : null;
  const updatedDate = typeof job.updatedAt === 'number' && job.updatedAt > 0 ? job.updatedAt : null;
  const usesExternalApplication = Boolean(job.applyUrl)
    && (job.applyMethod === 'external' || job.applyMethod === 'redirect');
  const applicationHref = usesExternalApplication
    ? job.applyUrl as string
    : `/apply/${job.slug || job.id}`;
  const applicationAction = (
    <Button asChild className="w-full" size="lg">
      {usesExternalApplication ? (
        <a href={applicationHref} target="_blank" rel="noopener noreferrer">
          Continue to employer site
          <ExternalLink className="ml-2 h-4 w-4" aria-hidden="true" />
        </a>
      ) : <Link href={applicationHref}>Apply on VHire</Link>}
    </Button>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-28 md:pb-6">
      {/* Back Button */}
      <Link href="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" />
        Back to Jobs
      </Link>

      {/* Job Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 dark:from-slate-800 dark:to-slate-900 rounded-2xl p-6 text-white mb-6">
        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <CompanyLogo
            name={job.company || job.title}
            light={job.companyLogo}
            dark={job.companyLogoDark}
            className="rounded-lg bg-background p-1"
          />

          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold mb-2">{sanitizeText(job.title)}</h1>
            <div className="flex flex-wrap items-center gap-3 text-slate-300 text-sm">
              {job.company && (
                <span className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {job.company}
                </span>
              )}
              {locationLabel && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {locationLabel}
                </span>
              )}
              {postedDate && (
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Posted {formatTimeAgo(postedDate, now)}
                </span>
              )}
              {updatedDate && updatedDate !== postedDate && (
                <span className="flex items-center gap-1 text-emerald-300">
                  • Updated {formatTimeAgo(updatedDate, now)}
                </span>
              )}
            </div>

            {(employmentTypeLabel || experienceLevelLabel || workModeLabel) && (
              <div className="flex flex-wrap gap-2 mt-4">
                {employmentTypeLabel && (
                  <Badge variant="secondary" className="bg-white/10 text-white border-0">
                    {employmentTypeLabel}
                  </Badge>
                )}
                {experienceLevelLabel && (
                  <Badge variant="secondary" className="bg-white/10 text-white border-0">
                    {experienceLevelLabel}
                  </Badge>
                )}
                {workModeLabel && (
                  <Badge
                    variant="secondary"
                    className={workModeLabel === 'Remote' ? 'bg-emerald-500/20 text-emerald-300 border-0' : 'bg-white/10 text-white border-0'}
                  >
                    {workModeLabel}
                  </Badge>
                )}
              </div>
            )}
          </div>

        </div>
      </div>

      {job.fullyRemote && job.applicantCountries?.length ? <p className="mb-4 text-sm">Remote work available to applicants in: {job.applicantCountries.map(displayCountry).join(', ')}</p> : null}
      {(job.searchLocations?.length ?? 0) > 1 ? <p className="mb-4 text-sm">Work locations: {job.searchLocations!.map(l => [l.city, l.state, displayCountry(l.country)].filter(Boolean).join(', ')).join(' · ')}</p> : null}
      {/* Quick Info Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Salary</p>
              <p className="font-semibold text-sm">{formatJobSalary(job)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Experience</p>
              <p className="font-semibold text-sm">{formatExperience(job.experienceRange, job.experienceLevel)}</p>
            </div>
          </CardContent>
        </Card>
        {job.vhireViewCount != null && (
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">VHire page views</p>
                <p className="font-semibold text-sm">{job.vhireViewCount.toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
        )}
        {job.vhireApplicantCount != null && (
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">VHire applicants</p>
                <p className="font-semibold text-sm">{job.vhireApplicantCount.toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {(job.vhireViewCount != null || job.vhireApplicantCount != null) && (
        <p className="text-xs text-muted-foreground mb-6">
          Activity recorded on VHire only. Page views are not unique visitors; applicants from employer sites and ATS systems are not included.
        </p>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Job Details */}
        <div className="lg:col-span-2 space-y-4">
          {/* About */}
          <CollapsibleSection
            title="About this role"
            icon={<Briefcase className="w-5 h-5 text-primary" />}
          >
            {/* Render HTML description safely */}
            <div
              className="text-muted-foreground prose prose-sm dark:prose-invert max-w-none
                [&_.jd-content]:space-y-1
                [&_.jd-row]:py-1
                [&_p]:my-1 [&_ul]:my-2 [&_li]:my-1
                [&_p:empty]:hidden"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(job.description) }}
            />
          </CollapsibleSection>

          {/* Responsibilities */}
          {job.responsibilities && job.responsibilities.length > 0 && (
            <CollapsibleSection
              title="Responsibilities"
              icon={<Users className="w-5 h-5 text-primary" />}
            >
              <ul className="space-y-2">
                {job.responsibilities.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-muted-foreground">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </CollapsibleSection>
          )}

          {/* Requirements */}
          {job.requirements && job.requirements.length > 0 && (
            <CollapsibleSection
              title="Requirements"
              icon={<Briefcase className="w-5 h-5 text-primary" />}
            >
              <ul className="space-y-2">
                {job.requirements.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-muted-foreground">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </CollapsibleSection>
          )}

          {/* Benefits */}
          {job.benefits && job.benefits.length > 0 && (
            <CollapsibleSection
              title="Benefits"
              icon={<Globe className="w-5 h-5 text-primary" />}
              defaultOpen={false}
            >
              <ul className="space-y-2">
                {job.benefits.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-muted-foreground">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </CollapsibleSection>
          )}
        </div>

        {/* Right Column - Company & Apply */}
        <div className="space-y-4">
          {/* Apply Card */}
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-5">
              <p className="text-sm text-muted-foreground mb-4">
                {usesExternalApplication
                  ? 'This employer receives applications on an external website. You will leave VHire when you continue.'
                  : 'Your application is completed on VHire. Review the role requirements before starting.'}
              </p>
              {applicationAction}
            </CardContent>
          </Card>

          {/* Skills */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Required Skills</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-wrap gap-2">
                {job.skills.map((skill) => (
                  <Badge key={skill} variant="secondary">
                    {skill}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Company Info */}
          {job.companyInfo && (
            <CompanyInfoCard companyInfo={job.companyInfo} />
          )}
        </div>
      </div>

      {/* Bottom CTA - Mobile */}
      <div className="fixed bottom-[var(--mobile-nav-height)] left-0 right-0 p-4 bg-background border-t md:hidden">
        {applicationAction}
      </div>
    </div>
  );
}
